import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import {
    formatDisplayDate,
    formatPhoneDisplay,
} from '../../../../../../common/utils/format-utils.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { ClipboardList, Plus, X } from '../../../../../components/icon/solar-icons.js'
import { FieldRow, Section, SectionHeader } from '../../../../../components/section/section.js'
import { genderLabel, normalizeGender } from '../../../utils/locale-labels.js'
import { PHONE_PLACEHOLDER, phoneFieldNote } from '../../../utils/phone-field.js'
import type {
    EmergencyContact,
    EmployeeProfileData,
    RequestConfirmFn,
} from '../employee-profile-types.js'
import '../employee-profile-view.css'

// Gender is stored as the Estonian word (Humaans imports may carry the English one).
const GENDER_VALUES = ['', 'Mees', 'Naine', 'Muu']

interface PersonalSectionProps {
    employee: EmployeeProfileData
    emergencyContacts: EmergencyContact[]
    lang: Language
    onUpdatePersonal?: (data: Record<string, string>) => Promise<boolean>
    onAddEmergencyContact?: (data: {
        name: string
        relationship: string
        phone: string
    }) => Promise<boolean>
    onUpdateEmergencyContact?: (
        contactId: number,
        data: { name: string; relationship: string; phone: string },
    ) => Promise<boolean>
    onDeleteEmergencyContact?: (contactId: number) => Promise<boolean>
    requestConfirm: RequestConfirmFn
}

/**
 * "Isiklikud andmed" — private-person data & emergency contacts (TASK-438).
 * One "Muuda" in the section header opens the whole section for editing;
 * emergency contacts are managed inside that edit view (add via "+ Lisa
 * kontakt", delete per card — both apply immediately through their own
 * endpoints). Bank details moved to "Tasustamine ja boonused"; nationalities
 * moved here from Üldinfo.
 */
export const PersonalSection = ({
    employee,
    emergencyContacts,
    lang,
    onUpdatePersonal,
    onAddEmergencyContact,
    onUpdateEmergencyContact,
    onDeleteEmergencyContact,
    requestConfirm,
}: PersonalSectionProps) => {
    const ph = t.portal

    // Permission-driven (server-resolved per-target). Private-tier rows (ID
    // code, gender) gate separately from the manager-tier personal fields;
    // the section splits view from edit so a view-only grant renders read-only.
    const v = employee.viewer
    const canViewPersonal = v?.canViewPersonal ?? false
    const canEditPersonal = v?.canEditPersonal ?? false
    const canViewPrivate = v?.canViewPrivate ?? false
    const canEditPrivate = v?.canEditPrivate ?? false
    const canViewEmergency = v?.canViewEmergency ?? false
    const canEditEmergency = v?.canEditEmergency ?? false

    // The section also surfaces the PUBLIC personal fields (birthday, personal
    // phone, fun facts). Show it when the viewer can see at least one — the
    // manager-tier rows (home address, personal email) and private rows
    // (gender, ID code) each gate themselves so nothing else leaks.
    const hasPublicPersonal =
        canEditPersonal ||
        Boolean(employee.birth_date) ||
        Boolean(employee.personal_phone) ||
        Boolean(employee.fun_facts) ||
        Boolean(employee.nationalities)

    // Section edit mode state
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)

    // Emergency contact add/edit-form state (inside the edit view).
    // null = closed; 'new' = add form; number = editing that contact
    const [contactFormTarget, setContactFormTarget] = useState<'new' | number | null>(null)
    const [contactName, setContactName] = useState('')
    const [contactRelationship, setContactRelationship] = useState('')
    const [contactPhone, setContactPhone] = useState('')
    const [contactSubmitting, setContactSubmitting] = useState(false)

    const resetContactForm = () => {
        setContactFormTarget(null)
        setContactName('')
        setContactRelationship('')
        setContactPhone('')
    }

    const openContactEdit = (c: EmergencyContact) => {
        setContactName(c.name)
        setContactRelationship(c.relationship ?? '')
        setContactPhone(c.phone)
        setContactFormTarget(c.id)
    }

    const canEditSection =
        (canEditPersonal && !!onUpdatePersonal) ||
        (canEditEmergency && !!(onAddEmergencyContact || onUpdateEmergencyContact))

    if (!(canViewPersonal || hasPublicPersonal || canViewEmergency)) return null

    const openEdit = () => {
        setDraft({
            birth_date: employee.birth_date ?? '',
            nationalities: employee.nationalities ?? '',
            home_address: employee.home_address ?? '',
            personal_email: employee.personal_email ?? '',
            personal_phone: employee.personal_phone ?? '',
            fun_facts: employee.fun_facts ?? '',
            // Private-tier fields go into the draft only with the private EDIT
            // grant — the server rejects the whole PATCH if any submitted field
            // lacks edit permission, so a view-only grant must not submit them.
            ...(canEditPrivate
                ? {
                      gender: normalizeGender(employee.gender ?? ''),
                      personal_id_code: employee.personal_id_code ?? '',
                  }
                : {}),
        })
        resetContactForm()
        setIsEditing(true)
    }

    // Contact cards — edit/delete affordances only inside the edit view.
    const contactList = (withDelete: boolean) =>
        emergencyContacts.map((c) => (
            <div key={c.id} className="portal-profile__contact-card">
                <div>
                    <div className="portal-profile__contact-name">{c.name}</div>
                    <div className="portal-profile__contact-detail">
                        {c.relationship ? `${c.relationship} · ` : ''}
                        {formatPhoneDisplay(c.phone)}
                    </div>
                </div>
                {withDelete && (
                    <div className="portal-profile__edit-actions">
                        {onUpdateEmergencyContact && contactFormTarget === null && (
                            <Button
                                variant="tertiary"
                                size="small"
                                text={ph.profile.editContact(lang)}
                                onClick={() => openContactEdit(c)}
                            />
                        )}
                        {onDeleteEmergencyContact && (
                            <button
                                type="button"
                                className="portal-profile__contact-delete"
                                title={ph.profile.deleteContact(lang)}
                                onClick={() => {
                                    requestConfirm(ph.profile.confirmDeleteContact(lang), () => {
                                        // Don't leave the edit form open over a
                                        // deleted contact.
                                        if (contactFormTarget === c.id) resetContactForm()
                                        void onDeleteEmergencyContact(c.id)
                                    })
                                }}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                )}
            </div>
        ))

    const emergencyBlock = (withManage: boolean) => (
        <div className="portal-profile__comp-history">
            <div className="portal-profile__comp-header">
                <h4 className="portal-profile__subsection-title">
                    {ph.profile.emergencyContacts(emergencyContacts.length)(lang)}
                </h4>
                {withManage && onAddEmergencyContact && contactFormTarget === null && (
                    <Button
                        variant="tertiary"
                        size="small"
                        iconLeft={Plus}
                        text={ph.profile.addContact(lang)}
                        onClick={() => setContactFormTarget('new')}
                    />
                )}
            </div>

            {withManage && contactFormTarget !== null && (
                <div className="portal-profile__contact-form">
                    <div className="portal-profile__edit-field">
                        <label className="portal-profile__field-label">
                            {ph.profile.contactName(lang)} *
                        </label>
                        <TextField
                            value={contactName}
                            onChange={(value) => setContactName(value)}
                        />
                    </div>
                    <div className="portal-profile__edit-field">
                        <label className="portal-profile__field-label">
                            {ph.profile.contactRelationship(lang)}
                        </label>
                        <TextField
                            value={contactRelationship}
                            onChange={(value) => setContactRelationship(value)}
                        />
                    </div>
                    <div className="portal-profile__edit-field">
                        <label className="portal-profile__field-label">
                            {ph.profile.contactPhone(lang)} *
                        </label>
                        <TextField
                            inputProps={{ type: 'tel', maxLength: 32 }}
                            placeholder={PHONE_PLACEHOLDER}
                            value={contactPhone}
                            onChange={(value) => setContactPhone(value)}
                            note={phoneFieldNote(contactPhone, lang)}
                        />
                    </div>
                    <div className="portal-profile__edit-actions">
                        <Button
                            text={ph.profile.cancelEdit(lang)}
                            variant="secondary"
                            size="small"
                            isDisabled={contactSubmitting}
                            onClick={resetContactForm}
                        />
                        <Button
                            text={contactSubmitting ? '...' : ph.profile.saveChanges(lang)}
                            size="small"
                            isDisabled={
                                contactSubmitting || !contactName.trim() || !contactPhone.trim()
                            }
                            onClick={() => {
                                const data = {
                                    name: contactName.trim(),
                                    relationship: contactRelationship.trim(),
                                    phone: contactPhone.trim(),
                                }
                                const action =
                                    contactFormTarget === 'new'
                                        ? onAddEmergencyContact?.(data)
                                        : typeof contactFormTarget === 'number'
                                          ? onUpdateEmergencyContact?.(contactFormTarget, data)
                                          : undefined
                                if (!action) return
                                setContactSubmitting(true)
                                void action.then((ok) => {
                                    setContactSubmitting(false)
                                    if (ok) resetContactForm()
                                })
                            }}
                        />
                    </div>
                </div>
            )}

            {emergencyContacts.length === 0 && contactFormTarget === null ? (
                <div className="portal-profile__field-value">{ph.profile.noContacts(lang)}</div>
            ) : (
                contactList(withManage)
            )}
        </div>
    )

    return (
        <Section>
            <SectionHeader
                icon={ClipboardList}
                title={ph.profile.personalInfo(lang)}
                actions={
                    canEditSection &&
                    !isEditing && (
                        <Button
                            variant="secondary"
                            size="small"
                            text={ph.profile.editProfile(lang)}
                            onClick={openEdit}
                        />
                    )
                }
            />

            {isEditing ? (
                /* ── Edit mode ── */
                <>
                    {canEditPersonal && onUpdatePersonal && (
                        <div className="portal-profile__edit-form">
                            {(
                                [
                                    {
                                        key: 'gender',
                                        label: ph.profile.gender(lang),
                                        type: 'select' as const,
                                        // Stored values stay Estonian; only the labels follow the language.
                                        options: GENDER_VALUES.map((value) => ({
                                            value,
                                            label: value ? genderLabel(value, lang) : '—',
                                        })),
                                        priv: true,
                                    },
                                    {
                                        key: 'birth_date',
                                        label: ph.profile.birthDate(lang),
                                        inputType: 'date' as const,
                                    },
                                    {
                                        key: 'personal_id_code',
                                        label: ph.profile.personalIdCode(lang),
                                        maxLength: 20,
                                        priv: true,
                                    },
                                    {
                                        key: 'nationalities',
                                        label: ph.profile.nationalities(lang),
                                    },
                                    {
                                        key: 'home_address',
                                        label: ph.profile.homeAddress(lang),
                                    },
                                    {
                                        key: 'personal_email',
                                        label: ph.profile.personalEmail(lang),
                                        inputType: 'email' as const,
                                    },
                                    {
                                        key: 'personal_phone',
                                        label: ph.profile.personalPhone(lang),
                                        inputType: 'tel' as const,
                                        maxLength: 32,
                                        phone: true as const,
                                    },
                                    {
                                        key: 'fun_facts',
                                        label: ph.profile.funFacts(lang),
                                        type: 'textarea' as const,
                                    },
                                ] as const
                            )
                                .filter((field) => canEditPrivate || !('priv' in field))
                                .map((field) => (
                                    <div key={field.key} className="portal-profile__edit-field">
                                        <label className="portal-profile__field-label">
                                            {field.label}
                                        </label>
                                        {'type' in field && field.type === 'select' ? (
                                            <Select
                                                value={draft[field.key] ?? ''}
                                                onChange={(value) =>
                                                    setDraft((prev) => ({
                                                        ...prev,
                                                        [field.key]: value,
                                                    }))
                                                }
                                                options={'options' in field ? field.options : []}
                                            />
                                        ) : 'type' in field && field.type === 'textarea' ? (
                                            <TextField
                                                as="textarea"
                                                textAreaProps={{ rows: 3 }}
                                                value={draft[field.key] ?? ''}
                                                onChange={(value) =>
                                                    setDraft((prev) => ({
                                                        ...prev,
                                                        [field.key]: value,
                                                    }))
                                                }
                                            />
                                        ) : (
                                            <TextField
                                                inputProps={{
                                                    type:
                                                        'inputType' in field
                                                            ? field.inputType
                                                            : 'text',
                                                    maxLength:
                                                        'maxLength' in field
                                                            ? field.maxLength
                                                            : undefined,
                                                }}
                                                placeholder={
                                                    'phone' in field ? PHONE_PLACEHOLDER : undefined
                                                }
                                                value={draft[field.key] ?? ''}
                                                onChange={(value) =>
                                                    setDraft((prev) => ({
                                                        ...prev,
                                                        [field.key]: value,
                                                    }))
                                                }
                                                note={
                                                    'phone' in field
                                                        ? phoneFieldNote(
                                                              draft[field.key] ?? '',
                                                              lang,
                                                          )
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </div>
                                ))}
                        </div>
                    )}

                    {/* Emergency contacts stay manageable inside the edit view:
                        add/delete apply immediately through their own endpoints. */}
                    {canViewEmergency && emergencyBlock(canEditEmergency)}

                    <div className="portal-profile__edit-actions">
                        <Button
                            text={ph.profile.cancelEdit(lang)}
                            variant="secondary"
                            size="small"
                            isDisabled={saving}
                            onClick={() => setIsEditing(false)}
                        />
                        <Button
                            text={
                                saving
                                    ? ph.profile.savingProfile(lang)
                                    : ph.profile.saveProfile(lang)
                            }
                            size="small"
                            isDisabled={saving}
                            onClick={() => {
                                if (!(canEditPersonal && onUpdatePersonal)) {
                                    setIsEditing(false)
                                    return
                                }
                                setSaving(true)
                                void onUpdatePersonal(draft).then((ok) => {
                                    setSaving(false)
                                    if (ok) setIsEditing(false)
                                })
                            }}
                        />
                    </div>
                </>
            ) : (
                /* ── Read-only mode ── */
                <>
                    {canViewPrivate && (
                        <FieldRow label={ph.profile.gender(lang)}>
                            {employee.gender ? genderLabel(employee.gender, lang) : '—'}
                        </FieldRow>
                    )}
                    {(canEditPersonal || employee.birth_date) && (
                        <FieldRow label={ph.profile.birthDate(lang)}>
                            {employee.birth_date ? formatDisplayDate(employee.birth_date) : '—'}
                        </FieldRow>
                    )}
                    {canViewPrivate && (
                        <FieldRow label={ph.profile.personalIdCode(lang)}>
                            {employee.personal_id_code ?? '—'}
                        </FieldRow>
                    )}
                    {(canEditPersonal || employee.nationalities) && (
                        <FieldRow label={ph.profile.nationalities(lang)}>
                            {employee.nationalities ?? '—'}
                        </FieldRow>
                    )}
                    {canViewPersonal && (
                        <FieldRow label={ph.profile.homeAddress(lang)}>
                            {employee.home_address ?? '—'}
                        </FieldRow>
                    )}
                    {canViewPersonal && (
                        <FieldRow label={ph.profile.personalEmail(lang)}>
                            {employee.personal_email ?? '—'}
                        </FieldRow>
                    )}
                    {(canEditPersonal || employee.personal_phone) && (
                        <FieldRow label={ph.profile.personalPhone(lang)}>
                            {employee.personal_phone ? (
                                <a
                                    href={`tel:${employee.personal_phone}`}
                                    className="portal-profile__link"
                                >
                                    {formatPhoneDisplay(employee.personal_phone)}
                                </a>
                            ) : (
                                '—'
                            )}
                        </FieldRow>
                    )}
                    {canViewEmergency && emergencyBlock(false)}
                    {(canEditPersonal || employee.fun_facts) && (
                        <FieldRow label={ph.profile.funFacts(lang)} multiline>
                            {employee.fun_facts ?? '—'}
                        </FieldRow>
                    )}
                </>
            )}
        </Section>
    )
}
